import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Picker from 'emoji-picker-react';
import api from '../../../services/api';
import {
  Container,
  Row,
  Divider,
  Panel,
  Checkbox,
  Toggle,
  Input,
  Drawer,
  InputGroup,
  IconButton,
  Whisper,
  Tooltip,
  Button,
  CheckPicker,
} from 'rsuite';
import StarIcon from '@rsuite/icons/legacy/Star';
import GearIcon from '@rsuite/icons/Gear';
import CloseIcon from '@rsuite/icons/Close';
import SearchIcon from '@rsuite/icons/Search';
import { useAuth } from '../../../hooks/auth';
import TagRender from '../../../components/TagRender';
import ChatComponent from '../../../components/ChatComponent';
import BackdropLoading from '../../../components/BackdropLoading';
import chatbotIcon from '../../../assets/icons/chatbot-icon.png';
import { useCustomTheme } from '../../../hooks/useCustomTheme';
import { CustomButton } from '../../../components/CustomButton';
import { CustomInput } from '../../../components/CustomInput';
import chatbotTelefone from '../../../assets/smartphone.jpg';
import arrowSvg from '../../../assets/arrow.svg';
import botImg from '../../../assets/bot.png';
import './style.css';
import { initialConfigs, initialValues } from './constants';
import { style } from './styles';

const DRAWER_SIZE = 'xs';

const RESPONSE_TYPES = [
  {
    key: 'initial',
    label: 'Inicial',
    color: 'violet',
    description: 'Enviada automaticamente no primeiro contato do cliente.',
    match: (r) => !!r.firstContact,
  },
  {
    key: 'pending',
    label: 'Pedido aceito',
    color: 'cyan',
    description: 'Enviada automaticamente quando o pedido é recebido.',
    match: (r) => !!r.isPending,
  },
  {
    key: 'progress',
    label: 'Em preparação',
    color: 'orange',
    description: 'Enviada automaticamente quando o pedido entra em preparação.',
    match: (r) => !!r.isProgress,
  },
  {
    key: 'delivered',
    label: 'Saiu para entrega',
    color: 'green',
    description: 'Enviada automaticamente quando o pedido sai para entrega.',
    match: (r) => !!r.isDelivered,
  },
  {
    key: 'pickup',
    label: 'Pronto para retirada',
    color: 'yellow',
    description: 'Enviada automaticamente quando o pedido está pronto para retirada.',
    match: (r) => !!r.isPickUp,
  },
  {
    key: 'finished',
    label: 'Finalizado',
    color: 'green',
    description: 'Enviada automaticamente quando o pedido é finalizado.',
    match: (r) => !!r.isFinished,
  },
  {
    key: 'resume',
    label: 'Resumo do pedido',
    color: 'red',
    description: 'Enviada automaticamente como resumo do pedido recebido.',
    match: (r) => !!r.isResume,
  },
  {
    key: 'keyword',
    label: 'Palavras-chave',
    color: 'blue',
    description: 'Enviada quando o cliente digita uma das palavras-chave configuradas.',
    match: (r) =>
      !r.firstContact &&
      !r.isPending &&
      !r.isProgress &&
      !r.isDelivered &&
      !r.isFinished &&
      !r.isPickUp &&
      !r.isResume,
  },
];

const getResponseType = (response) =>
  RESPONSE_TYPES.find((t) => t.match(response)) ?? null;

/**
 * Variáveis disponíveis em todas as mensagens (cliente, estabelecimento,
 * formatação geral).
 */
const GENERAL_PLACEHOLDERS = [
  {
    value: '{{nome_cliente}}',
    label: 'Nome do cliente',
    tooltip: 'Nome do cliente que está conversando',
  },
  {
    value: '{{saudacao}}',
    label: 'Saudação',
    tooltip:
      'Saudação automática conforme o horário: Bom dia / Boa tarde / Boa noite',
  },
  {
    value: '{{dividir_texto}}',
    label: 'Separar mensagens',
    tooltip: 'Quebra o texto em várias mensagens enviadas em sequência',
  },
  {
    value: '{{cardapio}}',
    label: 'Cardápio',
    tooltip: 'Envia o cardápio completo no chat',
  },
  {
    value: '{{link_cardapio}}',
    label: 'Link do cardápio',
    tooltip: 'Link público do cardápio digital',
  },
  {
    value: '{{endereco_loja}}',
    label: 'Endereço da loja',
    tooltip: 'Endereço do estabelecimento',
  },
  {
    value: '{{numero_loja}}',
    label: 'Telefone da loja',
    tooltip: 'Telefone do estabelecimento',
  },
  {
    value: '{{formas_pagamento}}',
    label: 'Formas de pagamento',
    tooltip: 'Lista das formas de pagamento aceitas',
  },
  {
    value: '{{horario_funcionamento}}',
    label: 'Horários',
    tooltip: 'Horários de funcionamento do dia',
  },
];

/**
 * Variáveis exclusivas da mensagem `Resumo do pedido` (isResume=1).
 * Só aparecem no editor quando essa mensagem está selecionada — pra evitar
 * que o usuário insira placeholders sem contexto em outras mensagens.
 */
const RESUME_PLACEHOLDERS = [
  {
    value: '{{numero_pedido}}',
    label: 'Nº do pedido',
    tooltip: 'Identificador do pedido',
  },
  {
    value: '{{data_pedido}}',
    label: 'Data do pedido',
    tooltip: 'Data em que o pedido foi feito',
  },
  {
    value: '{{hora_pedido}}',
    label: 'Hora do pedido',
    tooltip: 'Horário em que o pedido foi feito',
  },
  {
    value: '{{telefone_cliente}}',
    label: 'Telefone do cliente',
    tooltip: 'Telefone informado pelo cliente',
  },
  {
    value: '{{endereco_cliente}}',
    label: 'Endereço do cliente',
    tooltip: 'Endereço de entrega do cliente',
  },
  {
    value: '{{forma_pagamento_pedido}}',
    label: 'Pagamento escolhido',
    tooltip: 'Forma de pagamento selecionada para este pedido',
  },
  {
    value: '{{produtos_pedido}}',
    label: 'Itens do pedido',
    tooltip: 'Lista formatada de todos os itens do pedido',
  },
  {
    value: '{{subtotal_pedido}}',
    label: 'Subtotal',
    tooltip: 'Valor dos produtos sem taxas',
  },
  {
    value: '{{taxa_entrega}}',
    label: 'Taxa de entrega',
    tooltip: 'Valor cobrado pela entrega',
  },
  {
    value: '{{total_pedido}}',
    label: 'Total',
    tooltip: 'Valor total do pedido',
  },
  {
    value: '{{troco_para}}',
    label: 'Troco para',
    tooltip: 'Valor para o qual o cliente precisa de troco',
  },
  {
    value: '{{data_envio}}',
    label: 'Data de envio',
    tooltip: 'Data e hora do envio da mensagem ao cliente',
  },
];

const milissegundosParaFormatoHora = (ms) => {
  const segundos = Math.floor(ms / 1000);
  const horas = Math.floor(segundos / 3600);
  const minutos = Math.floor((segundos % 3600) / 60);
  const segundosRestantes = segundos % 60;
  let out = '';
  if (horas > 0) out += `${horas}h `;
  if (minutos > 0 || horas > 0) out += `${minutos}min `;
  out += `${segundosRestantes}s`;
  return out;
};

const milissegundosParaMinutos = (ms) => ms / (60 * 1000);
const minutosParaMilissegundos = (min) => min * 60 * 1000;

const filtrarNumerosDoTexto = (texto) => {
  const numeros = String(texto).match(/\d+/g);
  const numeroCombinado = numeros ? parseInt(numeros.join(''), 10) : null;
  return numeroCombinado > 1440 ? 1440 : numeroCombinado;
};

// ─── Engine de template para mensagem de resumo ─────────────────────────────
//
// Suporta:
//   • {{var}}                    → substitui pelo valor
//   • {{#if cond}}...{{/if}}     → mostra o bloco só quando a condição é true
//
// Condições disponíveis (derivadas do tipo de entrega + forma de pagamento):
//   entrega | retirada | consumo_local | agendado
//   dinheiro | cartao | pix | vale | troco | taxa_entrega | endereco

const conditionsFromInfo = (info) => {
  const tipo = String(info.TipoEntrega || '').toUpperCase();
  const pag = String(info.FormaPagamento || '').toUpperCase();
  const taxa = Number(info.TaxaEntrega || 0);
  const troco = Number(info.Troco || 0);
  const total = Number(info.ValorTotal || 0) + taxa;
  return {
    entrega: ['EN', 'AG'].includes(tipo),
    retirada: ['RE', 'AGRE'].includes(tipo),
    consumo_local: tipo === 'LOCAL',
    agendado: ['AG', 'AGRE'].includes(tipo),
    dinheiro: pag === 'DINHEIRO',
    cartao: ['CARTAO', 'CREDITO', 'DEBITO'].includes(pag),
    pix: pag === 'PIX',
    vale: ['VR', 'VA'].includes(pag),
    troco: pag === 'DINHEIRO' && troco > total,
    taxa_entrega: taxa > 0,
    endereco: !!(info.Logradouro || info.EnderecoSemCadastro),
  };
};

const resolveBlocks = (text, conditions) => {
  if (!text) return '';
  // Resolve {{#if X}}...{{/if}} de forma não-gulosa, aceitando blocos aninhados? Não — manter simples (sem nesting).
  return text.replace(
    /\{\{#if\s+(\w+)\}\}([\s\S]*?)\{\{\/if\}\}/g,
    (_, key, body) => (conditions[key] ? body : ''),
  );
};

const fillTokens = (text, vars) => {
  if (!text) return '';
  return String(text).replace(/\{\{(\w+)\}\}/g, (_, k) => {
    const v = vars[k];
    return v === undefined || v === null ? '' : String(v);
  });
};

const formatBRL = (n) =>
  `R$ ${Number(n || 0).toFixed(2).replace('.', ',')}`;

const safeJSON = (raw, fb) => {
  if (raw == null) return fb;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw) ?? fb;
  } catch {
    return fb;
  }
};

const buildItemPreview = (item, variations) => {
  const v = variations || {};
  const tamanho = item.Tamanho ? fillTokens(v.tamanho, { descricao: item.Tamanho }) : '';
  const borda = item.Borda?.Descricao
    ? fillTokens(v.borda, { descricao: item.Borda.Descricao })
    : '';
  const sabores = (item.Sabores || [])
    .map((s) => fillTokens(v.sabor, { rotulo: s.label }))
    .join('');
  const adicionais = (item.Adicionais || [])
    .map((a) =>
      fillTokens(v.adicional, { quantidade: a.amount ?? 1, rotulo: a.label }),
    )
    .join('');
  const montagens = (item.Montagem || [])
    .map((m) =>
      fillTokens(v.montagem, { quantidade: m.quantidade ?? 1, rotulo: m.label }),
    )
    .join('');
  const observacao = item.Observacao
    ? fillTokens(v.observacao, { texto: item.Observacao })
    : '';
  const valor = parseFloat(item.Valor || 0);
  const qtd = parseInt(item.Quantidade, 10) || 0;
  return fillTokens(v.item, {
    quantidade: qtd,
    descricao: item.Descricao,
    tamanho,
    borda,
    precoUnitario: formatBRL(valor),
    subtotal: formatBRL(valor * qtd),
    sabores,
    adicionais,
    montagens,
    observacao,
  });
};

const renderEnderecoPreview = (info, v) => {
  if (info.Logradouro) {
    return fillTokens(v.endereco, {
      logradouro: info.Logradouro || '',
      numero: info.Numero ? `Nº ${info.Numero}` : '',
      complemento: info.Complemento ? `, ${info.Complemento}` : '',
      bairro: info.Bairro ? `, ${info.Bairro}` : '',
      cidade: info.Cidade ? `, ${info.Cidade}` : '',
      uf: info.Uf ? `/${info.Uf}` : '',
    });
  }
  if (info.EnderecoSemCadastro) {
    return fillTokens(v.enderecoSemCadastro || '{{texto}}', {
      texto: info.EnderecoSemCadastro,
    });
  }
  return v.enderecoSemDados || 'Não informado';
};

const renderResumePreview = (text, variationsRaw, scenario) => {
  const variations = safeJSON(variationsRaw, {});
  const { info, pedido } = scenario;
  const conditions = conditionsFromInfo(info);

  const subtotal = parseFloat(info.ValorTotal || 0);
  const taxa = parseFloat(info.TaxaEntrega || 0);
  const total = subtotal + taxa;

  const itemSep = variations.separadorItens || '\n';
  const produtos = (pedido || [])
    .map((it) => buildItemPreview(it, variations))
    .join(itemSep);

  const vars = {
    numero_pedido: info.Id || '',
    data_pedido: '06/05/2026',
    hora_pedido: '14:30',
    nome_cliente: info.Nome || 'Cliente',
    telefone_cliente: info.Telefone || '',
    endereco_cliente: renderEnderecoPreview(info, variations),
    forma_pagamento_pedido:
      variations.formasPagamento?.[String(info.FormaPagamento || '').toUpperCase()] ||
      info.FormaPagamento ||
      '',
    produtos_pedido: produtos,
    subtotal_pedido: formatBRL(subtotal),
    taxa_entrega: formatBRL(taxa),
    total_pedido: formatBRL(total),
    troco_para: conditions.troco ? formatBRL(info.Troco) : '',
    data_envio: '06/05/2026 14:30',
  };

  // 1) Resolve blocos condicionais. 2) Substitui placeholders.
  // 3) Colapsa linhas em branco em excesso pra ficar limpo.
  const step1 = resolveBlocks(text, conditions);
  const step2 = fillTokens(step1, vars);
  return step2.replace(/\n{3,}/g, '\n\n').trim();
};

// Cenários de pré-visualização do resumo — o usuário escolhe um pra ver
// como a mensagem fica em cada situação real.
const RESUME_PREVIEW_SCENARIOS = [
  {
    key: 'entrega-dinheiro',
    label: '🛵 Entrega + Dinheiro c/ troco',
    info: {
      Id: 1234,
      TipoEntrega: 'EN',
      FormaPagamento: 'DINHEIRO',
      Troco: 100,
      ValorTotal: 75.5,
      TaxaEntrega: 5,
      Logradouro: 'Rua das Flores',
      Numero: '123',
      Complemento: 'Apto 4B',
      Bairro: 'Centro',
      Cidade: 'São Paulo',
      Uf: 'SP',
      Nome: 'Maria Silva',
      Telefone: '(11) 99999-9999',
    },
    pedido: [
      {
        Quantidade: '1',
        Descricao: 'Pizza Calabresa',
        Tamanho: 'Grande',
        Borda: { Descricao: 'Catupiry' },
        Valor: 70.5,
        Sabores: [{ label: 'Mussarela' }],
        Adicionais: [{ label: 'Bacon', amount: 1 }],
        Montagem: [],
        Observacao: 'sem cebola',
      },
      {
        Quantidade: '1',
        Descricao: 'Refrigerante 2L',
        Valor: 5,
        Sabores: [],
        Adicionais: [],
        Montagem: [],
      },
    ],
  },
  {
    key: 'retirada-cartao',
    label: '🏪 Retirada + Cartão',
    info: {
      Id: 1235,
      TipoEntrega: 'RE',
      FormaPagamento: 'CREDITO',
      ValorTotal: 60,
      TaxaEntrega: 0,
      Nome: 'João Souza',
      Telefone: '(11) 88888-8888',
    },
    pedido: [
      {
        Quantidade: '2',
        Descricao: 'Hambúrguer Artesanal',
        Valor: 30,
        Sabores: [],
        Adicionais: [],
        Montagem: [],
      },
    ],
  },
  {
    key: 'local-pix',
    label: '🍽 Consumo no local + PIX',
    info: {
      Id: 1236,
      TipoEntrega: 'LOCAL',
      FormaPagamento: 'PIX',
      ValorTotal: 45,
      TaxaEntrega: 0,
      Nome: 'Ana Costa',
      Telefone: '(11) 77777-7777',
    },
    pedido: [
      {
        Quantidade: '1',
        Descricao: 'Prato Executivo',
        Valor: 45,
        Sabores: [],
        Adicionais: [],
        Montagem: [],
        Observacao: 'sem alface',
      },
    ],
  },
];

// Estrutura por seções da mensagem de Resumo. O usuário NÃO vê {{#if}};
// edita cada seção como um bloco nomeado e o sistema cuida de quando
// mostrar. Seções com `condition` são escondidas quando o pedido não bate.
//
// `group` agrupa seções relacionadas. Dentro de um grupo, seções ficam
// em linhas consecutivas (separador \n). Entre grupos diferentes, há
// linha em branco (separador \n\n). Isso garante espaçamento correto
// independentemente de quais condicionais aparecem.
const RESUME_SECTIONS = [
  {
    key: 'topo',
    group: 'topo',
    title: 'Topo da mensagem',
    description: 'Aparece sempre, no início.',
    icon: '🧾',
    condition: null,
    placeholders: ['numero_pedido', 'data_pedido', 'hora_pedido'],
    default: '*🧾 Pedido #{{numero_pedido}}*\n🗓 {{data_pedido}}  ⏰ {{hora_pedido}}',
  },
  {
    key: 'agendado',
    group: 'topo',
    title: 'Pedido agendado',
    description: 'Só aparece quando o pedido é agendado.',
    icon: '⏳',
    condition: 'agendado',
    placeholders: [],
    default: '⏳ *Pedido agendado*',
  },
  {
    key: 'cliente',
    group: 'cliente',
    title: 'Dados do cliente',
    description: 'Aparece sempre. Mostra o nome e telefone.',
    icon: '👤',
    condition: null,
    placeholders: ['nome_cliente', 'telefone_cliente'],
    default: '*👤 Cliente*\n{{nome_cliente}}\n📞 {{telefone_cliente}}',
  },
  {
    key: 'entrega',
    group: 'cliente',
    title: 'Endereço de entrega',
    description: 'Só aparece em pedidos para entrega.',
    icon: '📍',
    condition: 'entrega',
    placeholders: ['endereco_cliente'],
    default: '📍 {{endereco_cliente}}',
  },
  {
    key: 'retirada',
    group: 'cliente',
    title: 'Retirada no estabelecimento',
    description: 'Só aparece quando o cliente vai retirar no local.',
    icon: '🏪',
    condition: 'retirada',
    placeholders: [],
    default: '🏪 Retirada no estabelecimento',
  },
  {
    key: 'consumo_local',
    group: 'cliente',
    title: 'Consumo no local',
    description: 'Só aparece quando o cliente vai comer no estabelecimento.',
    icon: '🍽',
    condition: 'consumo_local',
    placeholders: [],
    default: '🍽 Consumo no local',
  },
  {
    key: 'pagamento',
    group: 'pagamento',
    title: 'Forma de pagamento',
    description: 'Aparece sempre.',
    icon: '💰',
    condition: null,
    placeholders: ['forma_pagamento_pedido'],
    default: '*💰 Pagamento*\n{{forma_pagamento_pedido}}',
  },
  {
    key: 'troco',
    group: 'pagamento',
    title: 'Linha de troco',
    description: 'Só aparece em pagamento com dinheiro quando o cliente precisa de troco.',
    icon: '💵',
    condition: 'troco',
    placeholders: ['troco_para'],
    default: '💵 Troco para: {{troco_para}}',
  },
  {
    key: 'subtotal',
    group: 'valores',
    title: 'Subtotal',
    description: 'Aparece sempre. Mostra o valor dos produtos.',
    icon: '💲',
    condition: null,
    placeholders: ['subtotal_pedido'],
    default: '*💲 Valores*\nProdutos: {{subtotal_pedido}}',
  },
  {
    key: 'taxa_entrega',
    group: 'valores',
    title: 'Taxa de entrega',
    description: 'Só aparece quando o pedido tem taxa de entrega.',
    icon: '🚚',
    condition: 'taxa_entrega',
    placeholders: ['taxa_entrega'],
    default: 'Entrega: {{taxa_entrega}}',
  },
  {
    key: 'total',
    group: 'valores',
    title: 'Total',
    description: 'Aparece sempre.',
    icon: '💲',
    condition: null,
    placeholders: ['total_pedido'],
    default: '*Total: {{total_pedido}}*',
  },
  {
    key: 'itens',
    group: 'itens',
    title: 'Itens do pedido',
    description: 'Aparece sempre. Lista de produtos.',
    icon: '📝',
    condition: null,
    placeholders: ['produtos_pedido'],
    default:
      '*📝 Itens do pedido*\n-----------------------------------\n{{produtos_pedido}}\n-----------------------------------',
  },
  {
    key: 'rodape',
    group: 'rodape',
    title: 'Rodapé',
    description: 'Aparece sempre, no final.',
    icon: '🔚',
    condition: null,
    placeholders: ['data_envio'],
    default: '`Data envio: {{data_envio}}`',
  },
];

const defaultResumeSections = () => {
  const out = {};
  RESUME_SECTIONS.forEach((s) => {
    out[s.key] = s.default;
  });
  return out;
};

// Compõe o `text` final a partir do mapa de seções:
//   • dentro de um grupo: separador \n (linhas consecutivas)
//   • entre grupos:       separador \n\n (uma linha em branco)
//   • condicionais ficam wrapeadas em {{#if X}}...{{/if}}
//   • a limpeza de \n excedentes acontece no render (renderResumePreview
//     no frontend, renderOrderMessageFromTemplate no Desktop).
const composeResumeText = (sections) => {
  const groups = [];
  let currentGroupName = null;
  RESUME_SECTIONS.forEach((s) => {
    if (s.group !== currentGroupName) {
      groups.push({ name: s.group, items: [] });
      currentGroupName = s.group;
    }
    groups[groups.length - 1].items.push(s);
  });

  const groupTexts = groups.map((g) => {
    const parts = g.items
      .map((s) => {
        const content = (sections?.[s.key] ?? '').trim();
        if (!content) return null;
        return s.condition
          ? `{{#if ${s.condition}}}${content}{{/if}}`
          : content;
      })
      .filter(Boolean);
    return parts.join('\n');
  });

  return groupTexts.filter((g) => g.length > 0).join('\n\n');
};

// Lookup de placeholders pra mostrar chips em cada seção com label amigável.
const PLACEHOLDER_BY_KEY = {};
[...GENERAL_PLACEHOLDERS, ...RESUME_PLACEHOLDERS].forEach((p) => {
  const k = p.value.replace(/[{}]/g, '');
  PLACEHOLDER_BY_KEY[k] = p;
});


/**
 * Variações agrupadas por intenção do usuário. Os rótulos (que é o que a maioria
 * das pessoas vai querer ajustar) ficam abertos por padrão; o resto fica
 * escondido como "avançado". Para campos com placeholders, em vez de uma
 * dica em texto, geramos chips clicáveis que inserem o {{token}} no cursor.
 */
const PLACEHOLDER_LABELS = {
  quantidade: 'Quantidade',
  descricao: 'Descrição',
  tamanho: 'Tamanho',
  borda: 'Borda',
  precoUnitario: 'Preço unitário',
  subtotal: 'Subtotal',
  sabores: 'Sabores',
  adicionais: 'Adicionais',
  montagens: 'Montagens',
  observacao: 'Observação',
  rotulo: 'Rótulo',
  texto: 'Texto',
  logradouro: 'Logradouro',
  numero: 'Número',
  complemento: 'Complemento',
  bairro: 'Bairro',
  cidade: 'Cidade',
  uf: 'UF',
  valorTroco: 'Valor do troco',
};

const VARIATION_GROUPS = [
  {
    key: 'rotulos',
    icon: '🏷',
    title: 'Rótulos exibidos no resumo',
    description:
      'Como o nome de cada tipo de entrega e forma de pagamento aparece para o cliente.',
    defaultOpen: true,
    fields: [
      {
        key: 'tiposEntrega',
        label: 'Tipos de entrega',
        type: 'object',
        fields: [
          { key: 'EN', label: 'Entrega no endereço' },
          { key: 'RE', label: 'Retirada no estabelecimento' },
          { key: 'LOCAL', label: 'Consumo no local' },
          { key: 'AG', label: 'Agendado (entrega)' },
          { key: 'AGRE', label: 'Agendado (retirada)' },
        ],
      },
      {
        key: 'formasPagamento',
        label: 'Formas de pagamento',
        type: 'object',
        fields: [
          { key: 'DINHEIRO', label: 'Dinheiro' },
          { key: 'CARTAO', label: 'Cartão' },
          { key: 'CREDITO', label: 'Cartão de crédito' },
          { key: 'DEBITO', label: 'Cartão de débito' },
          { key: 'PIX', label: 'PIX' },
          { key: 'VR', label: 'Vale Refeição' },
          { key: 'VA', label: 'Vale Alimentação' },
        ],
      },
    ],
  },
  {
    key: 'item',
    icon: '📦',
    title: 'Como aparece cada item do pedido',
    description:
      'Define o formato de cada produto na lista do resumo. Os campos menores (tamanho, borda, sabor, adicional, montagem, observação) são encaixados automaticamente dentro do modelo principal quando o produto tiver esses dados.',
    defaultOpen: false,
    fields: [
      {
        key: 'item',
        label: 'Modelo de cada produto',
        description:
          'Como cada produto da lista é montado. Os blocos extras só aparecem quando o produto realmente tiver tamanho, borda, sabores, etc.',
        example: '*1x Pizza Calabresa* Grande (`Borda Catupiry`)\nR$ 50,00 = R$ 50,00',
        type: 'textarea',
        rows: 3,
        chips: [
          'quantidade',
          'descricao',
          'tamanho',
          'borda',
          'precoUnitario',
          'subtotal',
          'sabores',
          'adicionais',
          'montagens',
          'observacao',
        ],
      },
      {
        key: 'tamanho',
        label: 'Tamanho do produto',
        description:
          'Aparece logo depois do nome quando o produto tem tamanho selecionado.',
        example: ' Grande',
        type: 'text',
        chips: ['descricao'],
      },
      {
        key: 'borda',
        label: 'Borda da pizza',
        description: 'Mostrado entre parênteses quando a pizza tem borda recheada.',
        example: ' (`Borda Catupiry`)',
        type: 'text',
        chips: ['descricao'],
      },
      {
        key: 'sabor',
        label: 'Sabores extras',
        description: 'Uma linha para cada sabor extra escolhido em pizzas multissabor.',
        example: '\n`+Sabor: Mussarela`',
        type: 'text',
        chips: ['rotulo'],
      },
      {
        key: 'adicional',
        label: 'Adicionais (extras pagos)',
        description:
          'Uma linha para cada adicional pago escolhido pelo cliente (ex.: bacon, queijo extra).',
        example: '\n`1x Extra Bacon`',
        type: 'text',
        chips: ['quantidade', 'rotulo'],
      },
      {
        key: 'montagem',
        label: 'Itens de montagem',
        description:
          'Uma linha para cada item de montagem do produto (escolhas em combos personalizáveis).',
        example: '\n`2x Adicional Refrigerante`',
        type: 'text',
        chips: ['quantidade', 'rotulo'],
      },
      {
        key: 'observacao',
        label: 'Observação do cliente',
        description:
          'Aparece quando o cliente escreve uma observação para aquele produto específico (ex.: "sem cebola").',
        example: '\n_sem cebola_',
        type: 'text',
        chips: ['texto'],
      },
    ],
  },
  {
    key: 'extra',
    icon: '📍',
    title: 'Endereço e troco',
    description:
      'Como o endereço do cliente e a linha de troco aparecem no resumo. Cada item do endereço é usado conforme o cliente tenha cadastro completo, parcial ou nenhum.',
    defaultOpen: false,
    fields: [
      {
        key: 'endereco',
        label: 'Endereço completo (cliente cadastrado)',
        description:
          'Formato usado quando o cliente já tem o endereço cadastrado no sistema com todos os campos.',
        example: 'Rua das Flores | Nº 123 | Comp. Apto 4B, Centro',
        type: 'textarea',
        rows: 2,
        chips: ['logradouro', 'numero', 'complemento', 'bairro', 'cidade', 'uf'],
      },
      {
        key: 'enderecoSemCadastro',
        label: 'Endereço enviado por texto',
        description:
          'Usado quando o cliente envia o endereço como texto livre, sem cadastrar. Normalmente basta {{texto}} para repetir o que ele escreveu.',
        example: 'Rua das Flores, 123 - Centro',
        type: 'text',
        chips: ['texto'],
      },
      {
        key: 'enderecoSemDados',
        label: 'Quando não há endereço',
        description:
          'Texto exibido quando o pedido não tem endereço algum (ex.: retirada no balcão sem dados de entrega).',
        example: 'Não informado',
        type: 'text',
      },
      {
        key: 'linhaTroco',
        label: 'Linha de troco',
        description:
          'Aparece logo abaixo da forma de pagamento somente quando o cliente paga em dinheiro e pediu troco.',
        example: 'Troco para: R$ 100,00',
        type: 'text',
        chips: ['valorTroco'],
      },
    ],
  },
];

const safeParseVariations = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw) ?? {};
  } catch {
    return {};
  }
};

const RESPONSE_TYPE_HEX = {
  initial: '#a25dd1',
  pending: '#06b1d9',
  progress: '#f59e0b',
  delivered: '#16a34a',
  pickup: '#eab308',
  finished: '#16a34a',
  resume: '#dc2626',
  keyword: '#3b82f6',
};


const currentClock = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes(),
  ).padStart(2, '0')}`;
};

const ChatPreview = ({ text, phoneFrame = false }) => {
  const clock = currentClock();
  return (
    <div className={phoneFrame ? 'wa-preview phone' : 'wa-preview inline'}>
      {phoneFrame && (
        <>
          <div className="wa-notch" />
          <div className="wa-speaker" />
        </>
      )}

      <div className="wa-topbar">
        {phoneFrame && (
          <div className="wa-statusbar">
            <span className="wa-statusbar-time">{clock}</span>
            <div className="wa-statusbar-right">
              <svg viewBox="0 0 24 18" width="18" height="13" aria-hidden>
                <path
                  fill="currentColor"
                  d="M12 18l3.5-4.5c-1-.8-2.2-1.3-3.5-1.3s-2.5.5-3.5 1.3L12 18zm6-7.5c-1.7-1.3-3.7-2-6-2s-4.3.7-6 2l1.7 1.7c1.2-1 2.7-1.7 4.3-1.7s3.1.7 4.3 1.7l1.7-1.7zm3-3C18.5 5.3 15.4 4 12 4S5.5 5.3 3 7.5l1.7 1.7C6.6 7.6 9.2 6.5 12 6.5s5.4 1.1 7.3 2.7L21 7.5z"
                />
              </svg>
              <span className="wa-statusbar-battery">
                <span className="wa-statusbar-battery-fill" />
              </span>
            </div>
          </div>
        )}

        <div className="wa-header">
          <svg
            className="wa-header-back"
            viewBox="0 0 24 24"
            width="22"
            height="22"
            aria-hidden
          >
            <path
              fill="currentColor"
              d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20z"
            />
          </svg>
          <img src={botImg} alt="bot" className="wa-header-avatar" />
          <div className="wa-header-identity">
            <div className="wa-header-name">MenuCheff Bot</div>
            <div className="wa-header-status">online</div>
          </div>
          <div className="wa-header-icons">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden>
              <path
                fill="currentColor"
                d="M20 15.5c-1.25 0-2.45-.2-3.57-.57-.35-.11-.74-.03-1.02.25l-2.2 2.2a15.05 15.05 0 0 1-6.59-6.58l2.2-2.21c.28-.27.36-.66.25-1.02A11.36 11.36 0 0 1 8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z"
              />
            </svg>
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
              <circle fill="currentColor" cx="12" cy="5.5" r="1.8" />
              <circle fill="currentColor" cx="12" cy="12" r="1.8" />
              <circle fill="currentColor" cx="12" cy="18.5" r="1.8" />
            </svg>
          </div>
        </div>
      </div>

      <div className="wa-body">
        <div className="wa-date-chip">HOJE</div>
        <ChatComponent text={text} />
      </div>

      <div className="wa-footer">
        <div className="wa-input-pill">
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
            <path
              fill="#54656f"
              d="M9.153 11.603c.795 0 1.439-.879 1.439-1.962s-.644-1.962-1.439-1.962-1.439.879-1.439 1.962.644 1.962 1.439 1.962zm-3.204 1.362c-.026-.307-.131 5.218 6.063 5.551 6.066-.25 6.066-5.551 6.066-5.551-6.078 1.416-12.129 0-12.129 0zm11.363 1.108s-.669 1.959-5.051 1.959c-3.505 0-5.388-1.164-5.607-1.959 0 0 5.912 1.055 10.658 0zM11.804 1.011C5.609 1.011.978 6.033.978 12.228s4.826 10.761 11.021 10.761S23.02 18.423 23.02 12.228c.001-6.195-5.021-11.217-11.216-11.217zM12 21.354c-5.273 0-9.381-3.886-9.381-9.159s3.942-9.548 9.215-9.548 9.548 4.275 9.548 9.548c-.001 5.272-4.109 9.159-9.382 9.159zm3.108-9.751c.795 0 1.439-.879 1.439-1.962s-.644-1.962-1.439-1.962-1.439.879-1.439 1.962.644 1.962 1.439 1.962z"
            />
          </svg>
          <span className="wa-input-placeholder">Mensagem</span>
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
            <path
              fill="#54656f"
              d="M15.9 14.3H15l-.3-.3c1-1.1 1.6-2.7 1.6-4.3 0-3.7-3-6.7-6.7-6.7S3 6 3 9.7s3 6.7 6.7 6.7c1.7 0 3.2-.6 4.3-1.6l.3.3v.8l5.1 5.1 1.5-1.5-5-5.2zm-6.2 0c-2.6 0-4.6-2.1-4.6-4.6s2.1-4.6 4.6-4.6 4.6 2.1 4.6 4.6-2 4.6-4.6 4.6z"
            />
          </svg>
        </div>
        <button type="button" className="wa-mic" aria-label="gravar">
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
            <path
              fill="#fff"
              d="M12 15.5A3.5 3.5 0 0 0 15.5 12V6a3.5 3.5 0 0 0-7 0v6a3.5 3.5 0 0 0 3.5 3.5zm6-3.5a6 6 0 0 1-12 0H4a8 8 0 0 0 7 7.93V22h2v-2.07A8 8 0 0 0 20 12z"
            />
          </svg>
        </button>
      </div>

    </div>
  );
};

export default function Chatbot() {
  const { theme } = useCustomTheme();
  const { id_empresa } = useAuth();
  const [chatbotSteps, setChatbotSteps] = useState(initialValues);
  const [loadingPage, setLoadingPage] = useState(false);
  const [configs, setConfigs] = useState(initialConfigs);
  const [existValues, setExistValues] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const emojiPickerRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState();
  const [onlyFavorite, setOnlyFavorite] = useState(false);
  const [onlyEnabled, setOnlyEnabled] = useState(true);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [previewScenarioKey, setPreviewScenarioKey] = useState(
    RESUME_PREVIEW_SCENARIOS[0].key,
  );

  const previewScenario =
    RESUME_PREVIEW_SCENARIOS.find((s) => s.key === previewScenarioKey) ||
    RESUME_PREVIEW_SCENARIOS[0];

  const selectedCardValue = useMemo(() => {
    if (selectedCard === null) return null;
    return chatbotSteps.find((c) => +c.Id === +selectedCard) ?? null;
  }, [selectedCard, chatbotSteps]);
  const IsEdit = !!selectedCardValue;

  // Texto efetivo da prévia: para a mensagem `Resumo do pedido` aplicamos
  // os blocos condicionais + variações + dados de cenário, pra mostrar
  // exatamente como ficaria a mensagem em uma situação real. Para o resto
  // das mensagens, mostramos o texto cru (placeholders só são resolvidos
  // pelo bot na hora do envio).
  // Mapa de seções (origem da verdade pra mensagem isResume). Vem de
  // variations.sections; se não existir, usa defaults.
  const resumeSections = useMemo(() => {
    if (!selectedCardValue?.isResume) return null;
    const variations = safeParseVariations(selectedCardValue.variations);
    if (variations.sections && typeof variations.sections === 'object') {
      // Garante que todas as keys do schema existem (caso novas tenham sido adicionadas).
      const merged = defaultResumeSections();
      Object.keys(variations.sections).forEach((k) => {
        if (k in merged) merged[k] = variations.sections[k];
      });
      return merged;
    }
    return defaultResumeSections();
  }, [selectedCardValue]);

  const updateResumeSection = (key, value) => {
    if (!selectedCardValue) return;
    const newSections = { ...resumeSections, [key]: value };
    const newText = composeResumeText(newSections);
    setChatbotSteps((prev) =>
      prev.map((s) => {
        if (+s.Id !== +selectedCardValue.Id) return s;
        const variations = safeParseVariations(s.variations);
        variations.sections = newSections;
        return {
          ...s,
          text: newText,
          variations: JSON.stringify(variations),
        };
      }),
    );
  };

  // Texto efetivo da prévia: para a mensagem `Resumo do pedido` aplicamos
  // os blocos condicionais + variações + dados de cenário, pra mostrar
  // exatamente como ficaria a mensagem em uma situação real. Para o resto
  // das mensagens, mostramos o texto cru (placeholders só são resolvidos
  // pelo bot na hora do envio).
  const previewText = useMemo(() => {
    if (!selectedCardValue) return '';
    if (!selectedCardValue.isResume) return selectedCardValue.text || '';
    // Recompõe a partir das seções pra garantir coerência mesmo se text
    // estiver desatualizado em relação a variations.sections.
    const composed = resumeSections
      ? composeResumeText(resumeSections)
      : selectedCardValue.text || '';
    return renderResumePreview(
      composed,
      selectedCardValue.variations,
      previewScenario,
    );
  }, [selectedCardValue, previewScenario, resumeSections]);

  const updateStep = useCallback((Id, patch) => {
    setChatbotSteps((prev) =>
      prev.map((s) => (+s.Id === +Id ? { ...s, ...patch } : s)),
    );
  }, []);

  const changeKeywords = (Id, array) =>
    updateStep(Id, { keywords: JSON.stringify(array) });

  const setVariation = (Id, key, value) => {
    setChatbotSteps((prev) =>
      prev.map((s) => {
        if (+s.Id !== +Id) return s;
        const obj = safeParseVariations(s.variations);
        obj[key] = value;
        return { ...s, variations: JSON.stringify(obj) };
      }),
    );
  };

  const setVariationField = (Id, key, subKey, value) => {
    setChatbotSteps((prev) =>
      prev.map((s) => {
        if (+s.Id !== +Id) return s;
        const obj = safeParseVariations(s.variations);
        obj[key] = { ...(obj[key] ?? {}), [subKey]: value };
        return { ...s, variations: JSON.stringify(obj) };
      }),
    );
  };

  const insertVariationChip = (Id, fieldKey, placeholderKey) => {
    const input = document.getElementById(`variation-${fieldKey}`);
    const token = `{{${placeholderKey}}}`;
    if (!input) return;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const charBefore = input.value[start - 1];
    const charAfter = input.value[end];
    const sepBefore = charBefore === '}' ? ' ' : '';
    const sepAfter = charAfter === '{' ? ' ' : '';
    const insertText = sepBefore + token + sepAfter;
    const newValue =
      input.value.substring(0, start) + insertText + input.value.substring(end);
    setVariation(Id, fieldKey, newValue);
    requestAnimationFrame(() => {
      input.focus();
      const pos = start + sepBefore.length + token.length;
      input.selectionStart = pos;
      input.selectionEnd = pos;
    });
  };

  const insertSectionChip = (sectionKey, placeholderKey) => {
    const input = document.getElementById(`section-${sectionKey}`);
    const token = `{{${placeholderKey}}}`;
    if (!input) return;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const charBefore = input.value[start - 1];
    const charAfter = input.value[end];
    const sepBefore = charBefore === '}' ? ' ' : '';
    const sepAfter = charAfter === '{' ? ' ' : '';
    const insert = sepBefore + token + sepAfter;
    const newValue =
      input.value.substring(0, start) + insert + input.value.substring(end);
    updateResumeSection(sectionKey, newValue);
    requestAnimationFrame(() => {
      input.focus();
      const pos = start + insert.length;
      input.selectionStart = pos;
      input.selectionEnd = pos;
    });
  };

  const onEmojiClick = (Id, emoji) => {
    if (emoji) {
      setChatbotSteps((prev) =>
        prev.map((s) =>
          +s.Id === +Id ? { ...s, text: `${s.text}${emoji}` } : s,
        ),
      );
    }
    setShowPicker(false);
  };

  const onText = (Id, text) => updateStep(Id, { text });

  const inserirNoCursor = (Id, texto) => {
    const input = document.getElementById('chatMessage');
    if (!input || !texto) return;

    const hasSelection =
      typeof input.selectionStart === 'number' && input.selectionStart >= 0;
    const startPos = hasSelection ? input.selectionStart : input.value.length;
    const endPos = hasSelection ? input.selectionEnd : input.value.length;
    const charBefore = input.value[startPos - 1];
    const charAfter = input.value[endPos];
    const sepBefore = charBefore === '}' ? ' ' : '';
    const sepAfter = charAfter === '{' ? ' ' : '';
    const insertText = sepBefore + texto + sepAfter;
    const newValue =
      input.value.substring(0, startPos) +
      insertText +
      input.value.substring(endPos);

    updateStep(Id, { text: newValue });

    // reposiciona cursor logo após o texto inserido
    requestAnimationFrame(() => {
      input.focus();
      const pos = startPos + sepBefore.length + texto.length;
      input.selectionStart = pos;
      input.selectionEnd = pos;
    });
  };

  useEffect(() => {
    reloadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id_empresa]);

  useEffect(() => {
    if (!showPicker) return undefined;
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current?.contains(event.target) ||
        emojiButtonRef.current?.contains(event.target)
      ) {
        return;
      }
      setShowPicker(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPicker]);

  const resetValues = () => {
    setShowPicker(false);
    setSelectedCard(null);
  };

  const reloadPage = useCallback(async () => {
    setLoadingPage(true);
    try {
      const { data } = await api.get(`chatbot/find/${id_empresa}`);
      if (data.length === 0) {
        setExistValues(false);
      } else {
        const { chatbot, answer } = data;
        setChatbotSteps(answer);
        setConfigs(chatbot);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingPage(false);
    }
  }, [id_empresa]);

  const saveAllProps = async () => {
    const payload = {
      ...configs,
      companyId: id_empresa,
      chatbotanswers: chatbotSteps,
    };
    setExistValues(true);
    setLoadingPage(true);
    try {
      if (configs?.Id) {
        await api.put(`chatbot/update/${configs.Id}`, payload);
      } else {
        await api.post('chatbot/create', payload);
      }
      resetValues();
      await reloadPage();
    } catch (error) {
      console.log(error);
    }
    setTimeout(() => setLoadingPage(false), 500);
  };

  const favoriteMessage = (Id, isFavorite) => updateStep(Id, { isFavorite });

  const editMessage = (Id) => setSelectedCard(String(Id));

  const enableOrDisableMessage = async (Id, Ativo) => {
    updateStep(Id, { Ativo });
    if (!Id) return;
    try {
      await api.post(`chatbotanswers/update/${Id}`, { Ativo });
    } catch (error) {
      console.error('Falha ao atualizar status da mensagem:', error);
      updateStep(Id, { Ativo: !Ativo });
    }
  };

  const setFirstContact = (Id, firstContact) => {
    setChatbotSteps((prev) =>
      prev.map((s) => ({
        ...s,
        firstContact: +s.Id === +Id ? firstContact : false,
      })),
    );
  };

  const changeTitle = (Id, name) => updateStep(Id, { name });

  const changeConfigValues = (key, value) =>
    setConfigs((prevState) => ({ ...prevState, [key]: value }));

  const handleOpen = (key) => {
    setOpen(true);
    setPlacement(key);
  };

  const chageValueToResetMessage = (time) => {
    const number = filtrarNumerosDoTexto(time);
    changeConfigValues('intervalMessage', minutosParaMilissegundos(number));
  };

  const cancelEdit = () => setSelectedCard(null);

  const typeCounts = useMemo(() => {
    const counts = {};
    RESPONSE_TYPES.forEach((t) => {
      counts[t.key] = chatbotSteps.filter(t.match).length;
    });
    return counts;
  }, [chatbotSteps]);

  const typePickerData = RESPONSE_TYPES.map((t) => ({
    label: `${t.label} (${typeCounts[t.key] ?? 0})`,
    value: t.key,
  }));

  const visibleSteps = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return chatbotSteps.filter((response) => {
      if (onlyEnabled && !response.Ativo) return false;
      if (onlyFavorite && !response.isFavorite) return false;
      if (selectedTypes.length > 0) {
        const type = getResponseType(response);
        if (!type || !selectedTypes.includes(type.key)) return false;
      }
      if (q) {
        const haystack = `${response.name ?? ''} ${response.text ?? ''} ${
          response.keywords ?? ''
        }`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [chatbotSteps, onlyEnabled, onlyFavorite, selectedTypes, searchQuery]);

  const clearFilters = () => {
    setSelectedTypes([]);
    setOnlyFavorite(false);
    setOnlyEnabled(false);
    setSearchQuery('');
  };

  const hasActiveFilters =
    selectedTypes.length > 0 || onlyFavorite || onlyEnabled || !!searchQuery;

  const renderEmptyEditorPlaceholder = () => (
    <div className="editor-empty">
      <div className="editor-empty-art">
        <img src={arrowSvg} alt="" className="editor-empty-arrow" />
        <img src={chatbotTelefone} alt="" className="editor-empty-phone" />
      </div>
      <h5
        className="editor-empty-title"
        style={{ color: theme.colors.textSecondary }}
      >
        Selecione uma mensagem para editar
      </h5>
      <p
        className="editor-empty-subtitle"
        style={{ color: theme.colors.textSecondary }}
      >
        As alterações serão refletidas na prévia do celular em tempo real.
      </p>
    </div>
  );

  const renderList = () => (
    <section id="respostas" className="msg-list">
      <header className="msg-list-header">
        <div className="msg-list-title">
          <h5>Mensagens</h5>
          <span className="msg-list-count">{visibleSteps.length}</span>
        </div>
        <InputGroup inside size="sm" className="msg-list-search">
          <Input
            placeholder="Buscar"
            value={searchQuery}
            onChange={setSearchQuery}
          />
          <InputGroup.Addon>
            {searchQuery ? (
              <CloseIcon
                style={{ cursor: 'pointer' }}
                onClick={() => setSearchQuery('')}
              />
            ) : (
              <SearchIcon />
            )}
          </InputGroup.Addon>
        </InputGroup>
      </header>

      <div className="msg-list-body">
        {visibleSteps.length === 0 ? (
          <div className="msg-list-empty">
            <p>Nenhuma mensagem encontrada.</p>
            {hasActiveFilters && (
              <Button size="sm" appearance="link" onClick={clearFilters}>
                Limpar filtros
              </Button>
            )}
          </div>
        ) : (
          visibleSteps.map((response) => {
            const type = getResponseType(response);
            const isSelected = +selectedCard === +response.Id;
            const accent = type ? RESPONSE_TYPE_HEX[type.key] : '#94a3b8';
            return (
              <article
                key={response.Id}
                id={`panel_${response.Id}`}
                role="button"
                tabIndex={0}
                onClick={() => editMessage(response.Id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    editMessage(response.Id);
                  }
                }}
                className={[
                  'msg-card',
                  isSelected && 'is-selected',
                  !response.Ativo && 'is-disabled',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ '--card-accent': accent }}
              >
                <span className="msg-card-rail" aria-hidden />

                <div className="msg-card-body">
                  <div className="msg-card-row">
                    <h6 className="msg-card-title" title={response.name}>
                      {response.name}
                    </h6>
                  </div>

                  {type?.description && (
                    <p className="msg-card-preview" title={type.description}>
                      {type.description}
                    </p>
                  )}

                  <div className="msg-card-tags">
                    {type && (
                      <span
                        className="msg-card-tag msg-card-tag--type"
                        style={{ color: accent, borderColor: `${accent}55` }}
                      >
                        {type.label}
                      </span>
                    )}
                    {!response.Ativo ? (
                      <span className="msg-card-tag msg-card-tag--off">
                        Desativada
                      </span>
                    ) : null}
                  </div>
                </div>

                <div
                  className="msg-card-actions"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className={`msg-card-iconbtn ${
                      response.isFavorite ? 'is-active' : ''
                    }`}
                    onClick={() =>
                      favoriteMessage(response.Id, !response?.isFavorite)
                    }
                    aria-label={
                      response?.isFavorite ? 'Remover dos favoritos' : 'Favoritar'
                    }
                    title={
                      response?.isFavorite ? 'Remover dos favoritos' : 'Favoritar'
                    }
                  >
                    <StarIcon />
                  </button>
                  <Toggle
                    size="sm"
                    checked={!!response.Ativo}
                    onChange={() =>
                      enableOrDisableMessage(response.Id, !response.Ativo)
                    }
                  />
                </div>
              </article>
            );
          })
        )}
      </div>
    </section>
  );

  const renderEditor = () => (
    <Panel
      bordered
      className={IsEdit ? 'editor-panel editor-panel--editing' : 'editor-panel'}
      style={{
        borderRadius: '5px',
        backgroundColor: theme.colors.backgroundSecondary,
      }}
    >
      {!IsEdit ? (
        renderEmptyEditorPlaceholder()
      ) : (
        <div className="editor-layout">
          <div id="ChatComponentSmart" className="editor-phone">
            <ChatPreview text={previewText} phoneFrame />
            <p className="editor-phone-caption">Prévia</p>
            {selectedCardValue.isResume && (
              <div className="preview-scenario">
                <label className="preview-scenario-label">
                  Pré-visualizar como
                </label>
                <select
                  className="preview-scenario-select"
                  value={previewScenarioKey}
                  onChange={(e) => setPreviewScenarioKey(e.target.value)}
                >
                  {RESUME_PREVIEW_SCENARIOS.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="editor-pane">
            <div className="editor-card">
              <div className="editor-head">
                <button
                  type="button"
                  className="editor-back-btn"
                  onClick={cancelEdit}
                  aria-label="Voltar para a lista"
                  title="Voltar para a lista"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    aria-hidden
                  >
                    <path
                      fill="currentColor"
                      d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20z"
                    />
                  </svg>
                </button>
                <div className="editor-head-title">
                  <h5>{selectedCardValue.name || 'Mensagem'}</h5>
                  <div className="editor-head-meta">
                    {getResponseType(selectedCardValue) && (
                      <span className="msg-card-type">
                        <span
                          className="msg-card-dot"
                          style={{
                            background:
                              RESPONSE_TYPE_HEX[
                                getResponseType(selectedCardValue).key
                              ],
                          }}
                        />
                        {getResponseType(selectedCardValue).label}
                      </span>
                    )}
                    {selectedCardValue.firstContact ? (
                      <span className="msg-card-flag">Inicial</span>
                    ) : null}
                  </div>
                </div>
                <button
                  type="button"
                  className={`msg-card-btn ${
                    selectedCardValue?.isFavorite ? 'is-active' : ''
                  }`}
                  onClick={() =>
                    favoriteMessage(
                      selectedCardValue.Id,
                      !selectedCardValue?.isFavorite,
                    )
                  }
                  aria-label="favoritar"
                >
                  <StarIcon />
                </button>
              </div>
              <Divider style={{ margin: '0px 0px 20px 0px' }} />

              {(!!selectedCardValue?.isPending ||
                !!selectedCardValue?.isProgress ||
                !!selectedCardValue?.isDelivered ||
                !!selectedCardValue?.isFinished ||
                !!selectedCardValue?.isPickUp ||
                !!selectedCardValue?.isResume) && (
                <div className="editor-note">
                  {!!selectedCardValue.isPending &&
                    'Enviada automaticamente quando o pedido é recebido.'}
                  {!!selectedCardValue.isProgress &&
                    'Enviada automaticamente quando o pedido entra em preparação.'}
                  {!!selectedCardValue.isDelivered &&
                    'Enviada automaticamente quando o pedido sai para entrega.'}
                  {!!selectedCardValue.isFinished &&
                    'Enviada automaticamente quando o pedido é finalizado.'}
                  {!!selectedCardValue.isPickUp &&
                    'Enviada automaticamente quando o pedido está pronto para retirada.'}
                  {!!selectedCardValue.isResume &&
                    'Enviada automaticamente como resumo do pedido recebido.'}
                </div>
              )}

              <label className="field-label">Nome</label>
              <Input
                value={selectedCardValue.name}
                as="textarea"
                rows={2}
                onChange={(e) => changeTitle(selectedCardValue.Id, e)}
              />

              <div className="field-row">
                <Checkbox
                  checked={!!selectedCardValue?.firstContact}
                  onChange={() =>
                    setFirstContact(
                      selectedCardValue.Id,
                      !selectedCardValue?.firstContact,
                    )
                  }
                >
                  Usar como inicial
                </Checkbox>
                <Checkbox
                  checked={!!selectedCardValue?.Ativo}
                  onChange={() =>
                    enableOrDisableMessage(
                      selectedCardValue.Id,
                      !selectedCardValue?.Ativo,
                    )
                  }
                >
                  Resposta ativada
                </Checkbox>
              </div>

              <div id="ChatComponentDiv">
                <Divider style={{ margin: '0px 0px 20px 0px' }}>Prévia</Divider>
                {selectedCardValue.isResume && (
                  <div className="preview-scenario">
                    <label className="preview-scenario-label">
                      Pré-visualizar como
                    </label>
                    <select
                      className="preview-scenario-select"
                      value={previewScenarioKey}
                      onChange={(e) => setPreviewScenarioKey(e.target.value)}
                    >
                      {RESUME_PREVIEW_SCENARIOS.map((s) => (
                        <option key={s.key} value={s.key}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <ChatPreview text={previewText} />
              </div>

              {!selectedCardValue.isPending &&
                !selectedCardValue.isProgress &&
                !selectedCardValue.isDelivered &&
                !selectedCardValue.isFinished &&
                !selectedCardValue.isPickUp &&
                !selectedCardValue.isResume && (
                  <>
                    <Divider style={{ margin: '20px 0px 0px 0px' }} />
                    <div>
                      <label className="field-label">Palavras-chave</label>
                      <TagRender
                        valuesArray={JSON.parse(
                          selectedCardValue?.keywords || '[]',
                        )}
                        onChange={(e) =>
                          changeKeywords(selectedCardValue.Id, e)
                        }
                      />
                    </div>
                  </>
                )}

              {selectedCardValue?.variations != null && (
                <>
                  <Divider style={{ margin: '20px 0px 0px 0px' }} />
                  {(() => {
                    const data = safeParseVariations(
                      selectedCardValue.variations,
                    );
                    const renderField = (field) => {
                      if (field.type === 'object') {
                        const sub = data[field.key] ?? {};
                        return (
                          <div className="field-section" key={field.key}>
                            <label className="field-label">
                              {field.label}
                            </label>
                            <div className="variations-grid">
                              {field.fields.map((f) => (
                                <div className="variations-row" key={f.key}>
                                  <span className="variations-row-label">
                                    {f.label}
                                  </span>
                                  <Input
                                    size="sm"
                                    value={sub[f.key] ?? ''}
                                    onChange={(v) =>
                                      setVariationField(
                                        selectedCardValue.Id,
                                        field.key,
                                        f.key,
                                        v,
                                      )
                                    }
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      const value = data[field.key] ?? '';
                      return (
                        <div className="field-section" key={field.key}>
                          <label className="field-label">{field.label}</label>
                          {field.description && (
                            <p className="field-hint">{field.description}</p>
                          )}
                          {field.example && (
                            <p className="field-example">
                              <span className="field-example-label">
                                Aparece como:
                              </span>
                              <code>{field.example}</code>
                            </p>
                          )}
                          {field.chips && field.chips.length > 0 && (
                            <div className="variation-chips">
                              {field.chips.map((c) => (
                                <button
                                  type="button"
                                  key={c}
                                  className="variation-chip"
                                  onClick={() =>
                                    insertVariationChip(
                                      selectedCardValue.Id,
                                      field.key,
                                      c,
                                    )
                                  }
                                  title={`Inserir {{${c}}}`}
                                >
                                  + {PLACEHOLDER_LABELS[c] ?? c}
                                </button>
                              ))}
                            </div>
                          )}
                          {field.type === 'textarea' ? (
                            <Input
                              id={`variation-${field.key}`}
                              as="textarea"
                              rows={field.rows ?? 2}
                              value={value}
                              onChange={(v) =>
                                setVariation(
                                  selectedCardValue.Id,
                                  field.key,
                                  v,
                                )
                              }
                            />
                          ) : (
                            <Input
                              id={`variation-${field.key}`}
                              size="sm"
                              value={value}
                              onChange={(v) =>
                                setVariation(
                                  selectedCardValue.Id,
                                  field.key,
                                  v,
                                )
                              }
                            />
                          )}
                        </div>
                      );
                    };
                    return VARIATION_GROUPS.map((group) => (
                      <details
                        key={group.key}
                        className="variations"
                        open={group.defaultOpen}
                      >
                        <summary className="variations-summary">
                          {group.icon && (
                            <span className="variations-summary-icon" aria-hidden>
                              {group.icon}
                            </span>
                          )}
                          <div className="variations-summary-text">
                            <span className="variations-summary-title">
                              {group.title}
                            </span>
                            {group.description && (
                              <span className="variations-summary-hint">
                                {group.description}
                              </span>
                            )}
                          </div>
                          <span className="variations-summary-chevron" aria-hidden />
                        </summary>
                        <div className="variations-body">
                          {group.fields.map(renderField)}
                        </div>
                      </details>
                    ));
                  })()}
                </>
              )}

              <Divider />
              {selectedCardValue?.isResume && resumeSections ? (
                <div className="resume-sections">
                  <label className="field-label">Estrutura da mensagem</label>
                  <p className="field-hint">
                    Edite cada bloco direto no campo correspondente. Os
                    blocos marcados como condicionais aparecem somente
                    quando a situação do pedido bater com a regra (ex.: o
                    bloco de troco só aparece em pedidos pagos em dinheiro
                    com troco). Para esconder um bloco, basta deixar o
                    campo vazio.
                  </p>
                  {RESUME_SECTIONS.map((s) => (
                    <div className="resume-section" key={s.key}>
                      <div className="resume-section-head">
                        <div className="resume-section-title">
                          <span className="resume-section-icon">{s.icon}</span>
                          {s.title}
                        </div>
                        <span
                          className={`resume-section-badge ${
                            s.condition ? 'is-conditional' : 'is-always'
                          }`}
                        >
                          {s.condition ? 'condicional' : 'sempre aparece'}
                        </span>
                      </div>
                      <p className="resume-section-desc">{s.description}</p>
                      <Input
                        id={`section-${s.key}`}
                        as="textarea"
                        rows={s.key === 'itens' ? 5 : 2}
                        value={resumeSections[s.key] ?? ''}
                        onChange={(v) => updateResumeSection(s.key, v)}
                      />
                      {s.placeholders.length > 0 && (
                        <div className="resume-section-chips">
                          {s.placeholders.map((pk) => (
                            <button
                              type="button"
                              key={pk}
                              className="placeholder-btn placeholder-btn--resume"
                              onClick={() => insertSectionChip(s.key, pk)}
                              title={`Inserir {{${pk}}}`}
                            >
                              + {PLACEHOLDER_BY_KEY[pk]?.label ?? pk}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
              <div>
                <label className="field-label">Mensagem</label>
                <div style={{ display: 'flex', position: 'relative' }}>
                  <Input
                    value={selectedCardValue?.text}
                    onChange={(e) => onText(selectedCardValue.Id, e)}
                    id="chatMessage"
                    as="textarea"
                    rows={8}
                  />
                  <div
                    ref={emojiButtonRef}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '8px',
                      zIndex: 2,
                    }}
                  >
                    <Whisper
                      placement="bottom"
                      followCursor
                      speaker={<Tooltip>Adicione um emoji ao texto</Tooltip>}
                    >
                      <IconButton
                        onClick={() => setShowPicker((v) => !v)}
                        icon={
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="bi bi-emoji-laughing-fill"
                            viewBox="0 0 16 16"
                          >
                            <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zM7 6.5c0 .501-.164.396-.415.235C6.42 6.629 6.218 6.5 6 6.5c-.218 0-.42.13-.585.235C5.164 6.896 5 7 5 6.5 5 5.672 5.448 5 6 5s1 .672 1 1.5zm5.331 3a1 1 0 0 1 0 1A4.998 4.998 0 0 1 8 13a4.998 4.998 0 0 1-4.33-2.5A1 1 0 0 1 4.535 9h6.93a1 1 0 0 1 .866.5zm-1.746-2.765C10.42 6.629 10.218 6.5 10 6.5c-.218 0-.42.13-.585.235C9.164 6.896 9 7 9 6.5c0-.828.448-1.5 1-1.5s1 .672 1 1.5c0 .501-.164.396-.415.235z" />
                          </svg>
                        }
                        circle
                        size="md"
                      />
                    </Whisper>
                  </div>
                  {showPicker && (
                    <div
                      ref={emojiPickerRef}
                      id="emojiMessage"
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '52px',
                        zIndex: 3,
                      }}
                    >
                      <Picker
                        pickerStyle={{ width: '100%' }}
                        onEmojiClick={(e) =>
                          onEmojiClick(selectedCardValue.Id, e?.emoji)
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
              )}

              {!selectedCardValue?.isResume && (
              <div className="field-section">
                <label className="field-label">Inserir variável</label>
                <p className="field-hint">
                  Variáveis são substituídas pelos valores reais ao enviar a mensagem.
                </p>

                <div className="placeholder-group">
                  <span className="placeholder-group-label">Gerais</span>
                  <div className="placeholder-grid">
                    {GENERAL_PLACEHOLDERS.map((p) => (
                      <Whisper
                        key={p.value}
                        placement="top"
                        speaker={<Tooltip>{p.tooltip}</Tooltip>}
                      >
                        <button
                          type="button"
                          className="placeholder-btn"
                          onClick={() =>
                            inserirNoCursor(selectedCardValue.Id, p.value)
                          }
                        >
                          {p.label}
                        </button>
                      </Whisper>
                    ))}
                  </div>
                </div>

              </div>
              )}
            </div>

            <div className="editor-footer">
              <Button appearance="subtle" onClick={cancelEdit}>
                Cancelar
              </Button>
              <Button appearance="primary" onClick={saveAllProps}>
                Salvar alterações
              </Button>
            </div>
          </div>
        </div>
      )}
    </Panel>
  );

  return (
    <Container
      style={{
        padding: loadingPage ? 0 : '10px',
        backgroundColor: theme.colors.background,
      }}
    >
      {loadingPage ? (
        <BackdropLoading />
      ) : !existValues ? (
        <div style={style.notFoundBox}>
          <div style={style.divBox}>
            <img style={{ width: '100%' }} src={chatbotIcon} alt="chatbot" />
            <p
              style={{
                fontFamily: 'monospace',
                textAlign: 'center',
                margin: '15px 0px 15px 0px',
              }}
            >
              Você ainda não criou um fluxo de respostas para seu robô, deseja
              criar um agora?
            </p>
            <CustomButton onClick={saveAllProps} appearance="primary" active>
              Criar
            </CustomButton>
          </div>
        </div>
      ) : (
        <>
          <Drawer
            size={DRAWER_SIZE}
            placement={placement}
            open={open}
            onClose={() => setOpen(false)}
          >
            <Drawer.Header>
              <Drawer.Title>Configurações</Drawer.Title>
              <Drawer.Actions>
                <Button onClick={() => setOpen(false)}>Cancelar</Button>
                <Button
                  onClick={() => {
                    setOpen(false);
                    saveAllProps();
                  }}
                  appearance="primary"
                >
                  Salvar
                </Button>
              </Drawer.Actions>
            </Drawer.Header>
            <Drawer.Body className="settings-drawer">
              <div className="field-section">
                <label className="field-label">Intervalo de resposta</label>
                <p className="field-hint">
                  Tempo mínimo entre mensagens enviadas pelo robô.
                </p>
                <CustomInput
                  type="time"
                  value={configs.interval}
                  onChange={(e) => changeConfigValues('interval', e)}
                />
              </div>

              <div className="field-section">
                <label className="field-label">
                  Reenviar mensagem inicial após
                </label>
                <p className="field-hint">
                  Atualmente: {milissegundosParaFormatoHora(configs.intervalMessage)}
                </p>
                <CustomInput
                  type="number"
                  min="10"
                  max="1440"
                  value={milissegundosParaMinutos(configs.intervalMessage)}
                  onChange={chageValueToResetMessage}
                />
              </div>

              <div className="field-section">
                <Checkbox
                  checked={!!configs.ignoreGroups}
                  onChange={() =>
                    changeConfigValues('ignoreGroups', !configs.ignoreGroups)
                  }
                >
                  Ignorar mensagens de grupos
                </Checkbox>
              </div>
            </Drawer.Body>
          </Drawer>

          <Row className="show-grid" style={style.gridStyle}>
            <Panel
              header={
                <>
                  <h3 className="title">Respostas e configurações</h3>
                </>
              }
            >
              <div className="toolbar">
                <div className="toolbar-filters">
                  <CheckPicker
                    data={typePickerData}
                    value={selectedTypes}
                    onChange={setSelectedTypes}
                    placeholder="Tipo de resposta"
                    cleanable
                    searchable={false}
                    size="sm"
                    className="toolbar-picker"
                  />
                  <label className="toolbar-check">
                    <Checkbox
                      checked={onlyFavorite}
                      onChange={() => setOnlyFavorite(!onlyFavorite)}
                    >
                      Favoritas
                    </Checkbox>
                  </label>
                  <label className="toolbar-check">
                    <Checkbox
                      checked={onlyEnabled}
                      onChange={() => setOnlyEnabled(!onlyEnabled)}
                    >
                      Ativas
                    </Checkbox>
                  </label>
                  {hasActiveFilters && (
                    <Button
                      size="sm"
                      appearance="link"
                      onClick={clearFilters}
                    >
                      Limpar
                    </Button>
                  )}
                </div>
                <div className="toolbar-actions">
                  <span className="toolbar-count">
                    {visibleSteps.length} de {chatbotSteps.length}
                  </span>
                  <Button
                    size="sm"
                    appearance="ghost"
                    startIcon={<GearIcon />}
                    onClick={() => handleOpen('right')}
                  >
                    Configurações
                  </Button>
                </div>
              </div>

              <div
                id="card-container"
                className="cm-layout"
                style={{ backgroundColor: theme.colors.background }}
              >
                <aside
                  className={[
                    'cm-sidebar',
                    IsEdit && 'cm-sidebar--collapsed',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {renderList()}
                </aside>
                <section
                  className={[
                    'cm-main',
                    !IsEdit && 'cm-main--empty',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {renderEditor()}
                </section>
              </div>
            </Panel>
          </Row>
        </>
      )}
    </Container>
  );
}
